'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

const categories = [
  {
    icon: 'UserCircleIcon',
    color: '#FF5722',
    bg: '#FFF3EE',
    title: 'Getting Started',
    description: 'Account setup, verification, and first steps',
    faqs: [
      {
        q: 'How do I create a reviewer account?',
        a: 'Click "Join as Reviewer" on the homepage or visit /auth. Complete your profile with your name, email, phone number, and category interests. You\'ll need to verify your email before applying to campaigns.',
      },
      {
        q: 'What documents do I need for KYC verification?',
        a: 'You\'ll need a valid PAN card and a bank account or UPI ID for payouts. KYC is required before your first withdrawal. The process takes 24–48 hours.',
      },
      {
        q: 'Is there a minimum follower count to join?',
        a: 'No! IndianReviewClub welcomes reviewers of all sizes — from everyday consumers to large influencers. What matters is the quality and authenticity of your reviews.',
      },
      {
        q: 'How long does account approval take?',
        a: 'Most accounts are approved instantly. If additional verification is needed, it may take up to 48 hours. You\'ll receive an email notification once approved.',
      },
    ],
  },
  {
    icon: 'StarIcon',
    color: '#F59E0B',
    bg: '#FFFBEB',
    title: 'Reviews & Campaigns',
    description: 'How to apply, review products, and get approved',
    faqs: [
      {
        q: 'How do I apply for a campaign?',
        a: 'Browse available campaigns on the Products page. Click "Apply Now" on any campaign that matches your interests. Fill in the application form and submit. Brands review applications and select reviewers within 3–5 days.',
      },
      {
        q: 'What happens after I\'m selected for a campaign?',
        a: 'You\'ll receive an email and in-app notification. The brand will ship the product to your registered address. Once received, you have the campaign deadline to submit your review on the specified platform (Amazon, Flipkart, etc.) and upload proof.',
      },
      {
        q: 'What makes a review get approved?',
        a: 'Reviews must be genuine, detailed (minimum 100 words), include your honest opinion, and be posted on the specified platform. Our AI quality check verifies authenticity. Reviews that appear fake, copied, or incentivised without disclosure will be rejected.',
      },
      {
        q: 'Can I review a product I didn\'t like?',
        a: 'Absolutely — and we encourage it! Honest negative reviews are just as valuable as positive ones. Brands on our platform accept this. Attempting to post only positive reviews to please brands violates our policies.',
      },
    ],
  },
  {
    icon: 'BanknotesIcon',
    color: '#00BFA5',
    bg: '#E0F7F4',
    title: 'Earnings & Payouts',
    description: 'Wallet, withdrawals, UPI, and bank transfers',
    faqs: [
      {
        q: 'How much can I earn per review?',
        a: 'Earnings vary by campaign — typically ₹100 to ₹2,000 per review depending on product value, review platform, and campaign requirements. Premium campaigns for high-value products pay more.',
      },
      {
        q: 'When are earnings credited to my wallet?',
        a: 'Earnings are credited within 24–48 hours of your review being approved by our quality team. You\'ll receive a notification when funds are added to your wallet.',
      },
      {
        q: 'What is the minimum withdrawal amount?',
        a: 'The minimum withdrawal is ₹200. You can withdraw via UPI (instant) or bank transfer (3–5 business days). There are no withdrawal fees.',
      },
      {
        q: 'Is TDS deducted on my earnings?',
        a: 'Yes, TDS is deducted as per applicable Indian tax laws (typically 10% for earnings above ₹30,000 per year). You\'ll receive a Form 16A for your records. We recommend consulting a tax advisor for your specific situation.',
      },
    ],
  },
  {
    icon: 'BuildingStorefrontIcon',
    color: '#1A237E',
    bg: '#EEF0FB',
    title: 'For Brands',
    description: 'Campaigns, pricing, and managing reviews',
    faqs: [
      {
        q: 'How do I list a product for review?',
        a: 'Register as a seller at /seller-onboarding. Once your account is approved, you can create campaigns from your seller dashboard. Specify the product, review platform, number of reviewers needed, and campaign deadline.',
      },
      {
        q: 'How are reviewers selected for my campaign?',
        a: 'Reviewers apply to your campaign. You can view their profiles, review history, and ratings before selecting. You have full control over who reviews your product.',
      },
      {
        q: 'Can I request only positive reviews?',
        a: 'No. Our platform strictly prohibits incentivising positive-only reviews. Brands must accept honest reviews — positive or negative. This is what makes our reviews credible and valuable.',
      },
      {
        q: 'What is the typical turnaround time for a campaign?',
        a: 'Most campaigns complete within 2–4 weeks: 3–5 days for reviewer selection, 5–7 days for product delivery, and 7–14 days for review submission. You can set custom deadlines.',
      },
    ],
  },
];

export default function HelpPage() {
  const [showModal, setShowModal] = useState(false);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <>
      <Header onDownloadClick={() => setShowModal(true)} />
      <main className="min-h-screen bg-white pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden bg-white border-b border-gray-100 py-16 px-4">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-50 rounded-full blur-3xl opacity-60 translate-x-1/2 -translate-y-1/2" />
          </div>
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 text-orange-600 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-5">
              <Icon name="QuestionMarkCircleIcon" size={14} />
              Help Centre
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 leading-tight">
              How can we <span className="text-primary">help you?</span>
            </h1>
            <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
              Find answers to common questions about reviewing products, earning money, and running campaigns on IndianReviewClub.
            </p>
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
            {categories.map((cat) => (
              <a
                key={cat.title}
                href={`#${cat.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-all hover:-translate-y-0.5"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: cat.bg }}>
                  <Icon name={cat.icon as any} size={20} style={{ color: cat.color }} />
                </div>
                <p className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-primary transition-colors">{cat.title}</p>
                <p className="text-xs text-gray-400">{cat.description}</p>
              </a>
            ))}
          </div>

          {/* FAQ sections */}
          <div className="space-y-12">
            {categories.map((cat) => (
              <div key={cat.title} id={cat.title.toLowerCase().replace(/\s+/g, '-')}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: cat.bg }}>
                    <Icon name={cat.icon as any} size={18} style={{ color: cat.color }} />
                  </div>
                  <h2 className="font-heading font-bold text-xl text-gray-900">{cat.title}</h2>
                </div>
                <div className="space-y-3">
                  {cat.faqs.map((faq, i) => {
                    const key = `${cat.title}-${i}`;
                    const open = openFaq === key;
                    return (
                      <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(open ? null : key)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-900 text-sm pr-4">{faq.q}</span>
                          <Icon
                            name={open ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                            size={16}
                            className="text-gray-400 shrink-0"
                          />
                        </button>
                        {open && (
                          <div className="px-5 pb-5">
                            <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Still need help */}
        <section className="bg-[#F7F8FA] border-t border-gray-100 py-14 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl text-gray-900 mb-3">Still need help?</h2>
            <p className="text-gray-500 text-sm mb-6">
              Can&apos;t find what you&apos;re looking for? Our support team is here to help.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors text-sm"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={16} />
              Contact Support
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      {showModal && <AppDownloadModal onClose={() => setShowModal(false)} />}
    </>
  );
}
